import { createAction, createSelector, createSlice } from "@reduxjs/toolkit"
import {
   RealtimeProducer,
   RealtimeFeedPayload,
   RealtimeStatusPayload,
} from "@/hooks/useRealtimeConnection"
import { RootState } from "@/store"
import { LevelConfigurationItemMode } from "@/types/levelConfiguration"

export const PING_INTERVAL_MS = 15000
const PONG_LATENCY_ALLOWANCE_MS = 5000
export const REALTIME_STALE_AFTER_MS =
   PING_INTERVAL_MS + PONG_LATENCY_ALLOWANCE_MS

export const realtimeFeedReceived = createAction<RealtimeFeedPayload>(
   "realtime/feedReceived"
)

export const realtimeFeedsReceived = createAction<RealtimeFeedPayload[]>(
   "realtime/feedsReceived"
)

export const realtimeStatusChanged = createAction<RealtimeStatusPayload>(
   "realtime/statusChanged"
)

interface WebsocketState {
   connected: boolean
   authenticated: boolean
   lastError: string | null
   lastPongAt: number | null
}

export const initialState: WebsocketState = {
   connected: false,
   authenticated: false,
   lastError: null,
   lastPongAt: null,
}

const websocketSlice = createSlice({
   name: "websocket",
   initialState,
   reducers: {},
   extraReducers: (builder) => {
      builder.addCase(realtimeStatusChanged, (state, action) => {
         const { connected, authenticated, lastError, lastPongAt } =
            action.payload
         if (connected !== undefined) state.connected = connected
         if (authenticated !== undefined) state.authenticated = authenticated
         if (lastError !== undefined) state.lastError = lastError ?? null
         if (lastPongAt !== undefined) state.lastPongAt = lastPongAt ?? null
      })
   },
})

export default websocketSlice.reducer

export const selectIsRealtimeConnectionStale = (
   state: RootState,
   now: number
): boolean => {
   const { connected, lastPongAt } = state.websocketReducer
   if (lastPongAt === null) return false
   return !connected || now - lastPongAt > REALTIME_STALE_AFTER_MS
}

export const selectCellProducers = createSelector(
   (state: RootState) => state.facilitiesReducer.facilityDetails.levels,
   (state: RootState) => state.facilitiesReducer.facilityLevelSelectors,
   (levels, facilityLevelSelectors): RealtimeProducer[] => {
      const parentLevelId = facilityLevelSelectors?.at(-1)?.activeValue
      if (!parentLevelId || !levels?.length) return []
      return levels
         .filter(
            (level) =>
               level.parents?.includes(parentLevelId) && level.sensors?.length
         )
         .filter(
            (level) => level.type.mode === LevelConfigurationItemMode.PHYSICAL
         )
         .flatMap((level) =>
            level.sensors
               .filter((sensor) => Boolean(sensor.macAddress))
               .map((sensor) => ({
                  entityId: level.id,
                  macAddress: sensor.macAddress,
                  customIdentifier: sensor.serialNumber,
               }))
         )
   }
)

/**
 * Producers for the new floor overview — anchor/gateway sensors on virtual
 * levels (locations) that are direct children of the currently-selected floor.
 * The WS filter payload for these producers omits customIdentifier (empty
 * string means "send only macAddress").
 */
export const selectVirtualLevelProducers = createSelector(
   (state: RootState) => state.facilitiesReducer.facilityDetails.levels,
   (state: RootState) => state.facilitiesReducer.facilityLevelSelectors,
   (levels, facilityLevelSelectors): RealtimeProducer[] => {
      const parentLevelId = facilityLevelSelectors?.at(-1)?.activeValue
      if (!parentLevelId || !levels?.length) return []
      return levels
         .filter(
            (level) =>
               level.parents?.includes(parentLevelId) &&
               level.sensors?.length &&
               level.type.mode === LevelConfigurationItemMode.VIRTUAL
         )
         .flatMap((level) =>
            level.sensors
               .filter((sensor) => Boolean(sensor.macAddress))
               .map((sensor) => ({
                  entityId: level.id,
                  macAddress: sensor.macAddress,
                  customIdentifier: "",
               }))
         )
   }
)

export const selectFloorProducers = createSelector(
   selectCellProducers,
   selectVirtualLevelProducers,
   (cellProducers, anchorProducers): RealtimeProducer[] => [
      ...cellProducers,
      ...anchorProducers,
   ]
)
